import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import services from 'src/api/service';
import { createPortal } from 'react-dom';
import CustomButton from 'src/components/Actions/CustomButton';
import styles from './bcg-textual-alert-popup.module.scss';
import BCGTagInputBox from './bcg-tag-input-box';
import { useLocation } from 'react-router-dom';
import { sweetAlertHandler } from 'src/views/pages/targetting/toaster';
import Confirmation from 'src/common/Alert/Confirmation';
import { DEFAULT_CONFIRMATION_MODAL } from 'src/views/pages/text-categorization/textual-constant';
import GreenTick from 'src/assets/images/common/green-tick.svg';

export default function BCGSetup() {

  const profileDictionary = JSON.parse(sessionStorage.getItem('selectedProfileId'));
  const userDetails = JSON.parse(localStorage.getItem('User'));
  const brandConfigureIgnore = JSON.parse(localStorage.getItem('brandConfigureIgnore')) || {};
  const [doAlert, setDoAlert] = useState("");
  const [doLoading, setDoLoading] = useState(false);
  const location = useLocation();
  const filteredCustomTablesRef = useRef([]);

  const [confirmationModal, setConfirmationModal] = useState(DEFAULT_CONFIRMATION_MODAL);

  useEffect(() => {
    if(profileDictionary?.profileId && profileDictionary?.ams?.accessRevoked != true && profileDictionary?.ams?.connected == true && location.pathname.includes('amazon/targeting') && brandConfigureIgnore?.[profileDictionary?.profileId] != true) {
      services.postService({
        url: '/amazon/list-custom-table',
        payload: {
          profile_id: String(profileDictionary?.profileId),
        },
      }).then((response) => {
        let customTables = response?.data?.data || [];
        console.log('customTables', customTables);
        const brandConfigurationExists = ['Targeting - BCG', 'SearchTerm - BCG'].every((item) => customTables?.map((item) => item?.table_name).includes(item));
        filteredCustomTablesRef.current = customTables.filter((item) => item?.table_name === 'Targeting - BCG' || item?.table_name === 'SearchTerm - BCG');
        setDoAlert(brandConfigurationExists != true && brandConfigureIgnore?.[profileDictionary?.profileId] != true ? "brand-alert" : "");
      }).finally(() => {
        console.log('finally');
      });
    }
    return () => {
      setDoAlert("");
    }
  }, [location.pathname]);

  const doAction = useCallback(async (action, payload = {}) => {
    if (action === "remind") {
      const copyBrandConfigureIgnore = JSON.parse(localStorage.getItem('brandConfigureIgnore')) || {};
      copyBrandConfigureIgnore[profileDictionary?.profileId] = true;
      localStorage.setItem('brandConfigureIgnore', JSON.stringify(copyBrandConfigureIgnore));
      setDoAlert("");
    } else if (action === "update") {
      setDoAlert("brand-configuration");
    } else if (action === "update-brand-configuration") {
      try {
        setDoLoading(true);

        // Delete all existing custom tables sequentially
        for (const item of filteredCustomTablesRef.current) {
          await services.deleteService({
            url: '/amazon/delete-custom-table',
            nodeAPI: 1,
            payload: {
              table_id: item?.table_id,
              profile_id: String(profileDictionary?.profileId),
            },
          });
        }

        // Add keywords configuration
        const addKeywordsResponse = await services.postService({
          url: '/profiles/add-keywords',
          nodeAPI: 1,
          payload: {
            configuration: [payload],
          },
        });

        const { success: addKeywordsSuccess = false, message: addKeywordsMessage } = addKeywordsResponse?.data || {};

        if (!addKeywordsSuccess) {
          setDoLoading(false);
          sweetAlertHandler({
            type: 'alert',
            text: addKeywordsMessage || "Brand configuration not updated",
            showConfirmButton: true,
          });
          return;
        }

        // Add default custom table
        try {
          const addDefaultTableResponse = await services.postService({
            url: '/amazon/add-default-custom-table',
            payload: {
              profileId: String(profileDictionary?.profileId),
              email: String(profileDictionary?.profileOwner || userDetails?.email),
            },
          });

          const { success: addDefaultTableSuccess = false, message: addDefaultTableMessage } = addDefaultTableResponse?.data || {};

          if (addDefaultTableSuccess) {
            setDoLoading(false);
            setDoAlert("");
            const copyBrandConfigureIgnore = JSON.parse(localStorage.getItem('brandConfigureIgnore')) || {};
            copyBrandConfigureIgnore[profileDictionary?.profileId] = true;
            localStorage.setItem('brandConfigureIgnore', JSON.stringify(copyBrandConfigureIgnore));
            setConfirmationModal({
              show: true,
              icon: GreenTick,
              title: 'Brand + Competitor configuration was successfully created.',
              message: 'Two new tables, "Targeting - BCG" and "SearchTerm - BCG", have been created under search term and targeting reports in textual categorization.',
              hideCancel: true,
              confirmButtonText: "Ok",
              cancelButtonText: "Close",
              action: () => {
                window.location.reload();
              },
              cancelAction: () => {
                setConfirmationModal(DEFAULT_CONFIRMATION_MODAL);
              }
            });
          } else {
            setDoLoading(false);
            sweetAlertHandler({
              type: 'alert',
              text: addDefaultTableMessage || "Brand configuration not updated",
              showConfirmButton: true,
            });
          }
        } catch (error) {
          setDoLoading(false);
          setDoAlert("");
          sweetAlertHandler({
            type: 'alert',
            text: error?.message || "Brand configuration not updated",
            showConfirmButton: true,
          });
        }
      } catch (error) {
        setDoLoading(false);
        setDoAlert("");
        sweetAlertHandler({
          type: 'alert',
          text: error?.message || "Brand configuration not updated",
          showConfirmButton: true,
        });
      }
    }
  }, []);

  return (
    <>
      {doAlert == "brand-alert" && createPortal(<AccountDetailsPopup doAction={doAction} />, document.body)}
      {doAlert == "brand-configuration" && createPortal(<BrandConfigurationPopup doAction={doAction} doLoading={doLoading} />, document.body)}

      <Confirmation confirmationModal={confirmationModal} setConfirmationModal={setConfirmationModal} />
    </>
  );
}

function AccountDetailsPopup({ doAction }) {

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);

  return (
    <div className={styles['bcg-brand-config-overlay']}>
      <div className={styles['bcg-textual-alert-popup']}>
        <div className={styles['bcg-textual-alert-popup__header']}>
          <span style={{ display: 'flex', alignItems: 'center' }}><AlertIcon /></span>
          <span className={styles['bcg-textual-alert-popup__title']}>Update Your Account Details</span>
        </div>
        <div className={styles['bcg-textual-alert-popup__desc']}>
          To enable automated setup of Brand, Competitors & Generic (BCG) custom tables for Targeting and Search Terms, please update the following key account details
        </div>
        <ul className={styles['bcg-textual-alert-popup__list']}>
          <li className={styles['bcg-textual-alert-popup__list-item']}>
            <span className={styles['bcg-textual-alert-popup__list-label']}>Brand Name</span>
          </li>
          <li className={styles['bcg-textual-alert-popup__list-item']}>
            <span className={styles['bcg-textual-alert-popup__list-label']}>Competitor Names</span>
          </li>
        </ul>
        <div className={styles['bcg-textual-alert-popup__footer']}>
          Once updated, we will auto-generate two custom tables to enhance your targeting insights.
        </div>
        <div className={styles['bcg-textual-alert-popup__actions']}>
          <CustomButton
            outline
            className="bcg-btn-outline gap-6 border text-black d-flex align-items-center"
            boxShadow="0px 2.4px 4.2px 0px #0000000D"
            margin="0px"
            onClick={() => doAction("remind")}
          >
            <AlertClockIcon /> Remind Me Later
          </CustomButton>
          <CustomButton
            primaryBtn
            className="bcg-btn-primary"
            margin="0px"
            onClick={() => doAction("update")}
          >
            Update Now
          </CustomButton>
        </div>
      </div>
    </div>
  );
}

function BrandConfigurationPopup({ doAction, doLoading = false }) {
  const profileDictionary = JSON.parse(sessionStorage.getItem('selectedProfileId'));
  const [brandNames, setBrandNames] = useState([]);
  const [formData, setFormData] = useState({
    enteredBrand: '',
    enteredCompetitor: '',
  });
  const [competitorNames, setCompetitorNames] = useState([]);
  const [accountName] = useState(profileDictionary?.accountInfo?.name);

  const canSubmit = useMemo(() => {
    return (
      (brandNames.length > 0) &&
      (competitorNames.length > 0)
    );
  }, [brandNames, competitorNames]);

  // Disable body scroll when popup is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);

  return (
    <div className={styles['bcg-brand-config-overlay']}>
      <div className={styles['bcg-brand-config-popup']}>
        <div className={styles['bcg-brand-config-popup__header']}>
          Update Brand + Competitor Account Details
        </div>
        <div className={styles['bcg-brand-config-popup__field']}>
          <div className={styles['bcg-brand-config-popup__form-label']}>
            Account Name
          </div>
          <input
            className={styles['bcg-brand-config-popup__input']}
            value={accountName}
            disabled
          />
        </div>
        <hr className={styles['bcg-brand-config-popup__dotted-divider']} />
        <section className={styles['bcg-brand-config-popup__gray-section']}>
          <div className={styles['bcg-brand-config-popup__field']}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className={`${styles['bcg-brand-config-popup__form-label']} mb-0`}>
                Brand Names
              </div>
              {(brandNames.length > 0 ) && <button
                type="button"
                className="justify-content-end"
                style={{
                  color: '#1a77f2',
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
                onClick={() => {
                  setBrandNames([]);
                  setFormData({ ...formData, enteredBrand: '' });
                }}
              >
                Clear All
              </button>}
            </div>
            <BCGTagInputBox
              value={brandNames}
              onChange={setBrandNames}
              placeholder="Enter names"
              tagType="brand"
              onInputChange={(value) => setFormData({ ...formData, enteredBrand: value })}
              onClearAll={() => setBrandNames([])}
            />
          </div>
          <div className={styles['bcg-brand-config-popup__field']}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className={`${styles['bcg-brand-config-popup__form-label']} mb-0`}>
                Competitor Names
              </div>
              {(competitorNames.length > 0) && <button
                type="button"
                className="justify-content-end"
                style={{
                  color: '#1A77F2',
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
                onClick={() => {
                  setCompetitorNames([]);
                  setFormData({ ...formData, enteredCompetitor: '' });
                }}
              >
                Clear All
              </button>}
            </div>
            <BCGTagInputBox
              value={competitorNames}
              onChange={setCompetitorNames}
              placeholder="Enter names"
              tagType="competitor"
              onInputChange={(value) => setFormData({ ...formData, enteredCompetitor: value })}
              onClearAll={() => setCompetitorNames([])}
              minHeight="162px"
            />
          </div>
        </section>
        <hr className={styles['bcg-brand-config-popup__dotted-divider']} />
        <div className={styles['bcg-brand-config-popup__footer']}>
          <p className={styles['bcg-brand-config-popup__footer-text']}>Note: Please press key 'ENTER' after typing each brand or competitor names OR you can also copy and paste the names from excel.</p>
        </div>
        <div className={styles['bcg-brand-config-popup__actions']} key={String(brandNames.length) + String(competitorNames.length)+String(canSubmit)}>
          <CustomButton
            outline
            className="bcg-btn-outline border text-black gap-6"
            boxShadow="0px 2.4px 4.2px 0px #0000000D"
            margin="0px"
            padding="7px 25px"
            onClick={() => doAction("remind")}
          >
            <AlertClockIcon /> Remind Me Later
          </CustomButton>
          <CustomButton
            primaryBtn
            className="bcg-btn-primary"
            margin="0px"
            padding="8px 25px"
            disabled={!canSubmit || doLoading}
            onClick={() => doAction("update-brand-configuration", {
              profile_id: String(profileDictionary?.profileId),
              brands: brandNames,
              competitors: competitorNames
            })}
          >
            Submit
          </CustomButton>
        </div>
      </div>
    </div>
  );
}

export const AlertIcon = () => {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="30" height="30" rx="15" fill="#FFEAC0" />
      <path d="M15 5C9.50742 5 5 9.50742 5 15C5 20.4926 9.50742 25 15 25C20.4926 25 25 20.4926 25 15C25 9.50742 20.4926 5 15 5ZM16.1719 19.6875C16.1719 20.3335 15.646 20.8594 15 20.8594C14.354 20.8594 13.8281 20.3335 13.8281 19.6875V13.8281C13.8281 13.1821 14.354 12.6562 15 12.6562C15.646 12.6562 16.1719 13.1821 16.1719 13.8281V19.6875ZM15 11.4844C14.354 11.4844 13.8281 10.9585 13.8281 10.3125C13.8281 9.66648 14.354 9.14062 15 9.14062C15.646 9.14062 16.1719 9.66648 16.1719 10.3125C16.1719 10.9585 15.646 11.4844 15 11.4844Z" fill="#FCA747" />
    </svg>
  );
}

export const AlertClockIcon = () => {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.25 3.5V5.5L6.75 7M9.75 5.5C9.75 7.98528 7.73528 10 5.25 10C2.76472 10 0.75 7.98528 0.75 5.5C0.75 3.01472 2.76472 1 5.25 1C7.73528 1 9.75 3.01472 9.75 5.5Z" stroke="#51596C" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  );
}